<template>
  <div class="change-email-page">
    
    <div class="skip-links">
      <a href="#main-content" class="skip-link">Zum Hauptinhalt</a>
    </div>

    <div class="auth-container">
      <header>
        <div class="logo-container">
          <h1 class="app-title">Quitly</h1>
        </div>
        <h2 id="page-title" class="page-title">E-Mail ändern</h2>
      </header>
      
      <main id="main-content" role="main" aria-labelledby="page-title">
        <div v-if="isLoading" class="loading-container" aria-live="polite">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Wird geladen...</span>
          </div>
          <p class="mt-3">Anfrage wird verarbeitet...</p>
        </div>
        
        <div v-else-if="errorMessage" class="error-container" aria-live="assertive">
          <div class="error-icon" aria-hidden="true">
            <font-awesome-icon icon="circle-exclamation" size="4x" class="text-danger" />
          </div>
          <p class="error-text">{{ errorMessage }}</p>
          <div class="mt-4">
            <router-link to="/auth/login" class="btn btn-outline-primary me-2">
              {{ 'Zurück zur Anmeldung' }}
            </router-link>
          </div>
        </div>
        
        <div v-else-if="changeSuccess" class="success-container" aria-live="polite">
          <div class="success-icon" aria-hidden="true">
            <font-awesome-icon icon="circle-check" size="4x" class="text-success" />
          </div>
          <p class="success-text">E-Mail wurde erfolgreich geändert</p>
          <p class="info-text" v-if="newEmail">
            Deine neue E-Mail: <strong>{{ newEmail }}</strong>
          </p>
          <div class="mt-4">
            <router-link to="/auth/login" class="btn btn-primary">
              {{ 'Zur Anmeldung' }}
            </router-link>
          </div>
        </div>
        
        <div v-else-if="token" class="verification-pending-container">
          <div class="verify-icon" aria-hidden="true">
            <font-awesome-icon icon="envelope" size="4x" class="text-primary" />
          </div>
          <h3 class="verify-title mt-3">E-Mail bestätigen</h3>
          <p class="verify-text">Bitte bestätige deine neue E-Mail-Adresse</p>
          <div class="mt-4">
            <button 
              @click="confirmEmailChange" 
              class="btn btn-primary verify-button"
              :disabled="isVerifying"
              aria-busy="isVerifying"
            >
              <span v-if="isVerifying">
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {{ isVerifying ? 'Wird verarbeitet...' : 'Klicke hier zur Bestätigung' }}
              </span>
              <span v-else>
                {{ isVerifying ? 'Wird verarbeitet...' : 'Klicke hier zur Bestätigung' }}
              </span>
            </button>
          </div>
        </div>
        
        <div v-else class="error-container" aria-live="assertive">
          <div class="error-icon" aria-hidden="true">
            <font-awesome-icon icon="circle-exclamation" size="4x" class="text-warning" />
          </div>
          <p class="error-text">Token fehlt</p>
          <div class="mt-4">
            <router-link to="/auth/login" class="btn btn-outline-primary">
              {{ 'Zurück zur Anmeldung' }}
            </router-link>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { confirmEmailChange as confirmEmailChangeApi } from '@/api/auth'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const isLoading = ref(false)
const isVerifying = ref(false)
const errorMessage = ref('')
const changeSuccess = ref(false)
const newEmail = ref('')
const token = ref(null)

onMounted(async () => {
  if (authStore.isAuthenticated) {
    await authStore.logout()
  }

  token.value = route.query.token
  
  if (!token.value) {
    errorMessage.value = 'Token fehlt'
  }
})

const confirmEmailChange = async () => {
  if (!token.value) {
    errorMessage.value = 'Token fehlt'
    return
  }
  
  try {
    isVerifying.value = true
    const response = await confirmEmailChangeApi(token.value)
    changeSuccess.value = true
    newEmail.value = response.email
  } catch (error) {
    console.error('E-Mail-Änderung fehlgeschlagen:', error)
    errorMessage.value = error.response?.data?.message || 'E-Mail-Änderung fehlgeschlagen'
  } finally {
    isVerifying.value = false
  }
}
</script>
